import { useAnalytics } from "@solace/hooks";
import { Input, PrimaryBtn } from "components";
import { firebaseCodeErrorsMap } from "error-codes";
import { FirebaseError } from "firebase/app";
import { useFormik } from "formik";
import { useState } from "react";
import * as Yup from "yup";
import { SocialSignOn } from "../social-sign-on";

type LoginFormProps = {
  children?: React.ReactNode;
  onSuccess?: () => void;
  onError?: (err: Error) => void;
};

export const LoginForm = ({ onSuccess, onError, children }: LoginFormProps) => {
  const { analytics } = useAnalytics();
  const [submitError, setSubmitError] = useState<string | undefined>();

  const { handleSubmit, handleChange, values, errors, isSubmitting } =
    useFormik({
      initialValues: {
        email: "",
        password: "",
      },
      validationSchema: Yup.object().shape({
        email: Yup.string().email().required(),
        password: Yup.string().required(),
      }),
      onReset() {
        setSubmitError(undefined);
      },
      async onSubmit(values) {
        try {
          // Dynamically import firebase. This is a heavy package and thus this saves us
          // from importing unnecessarily
          const { getAuth, signInWithEmailAndPassword } = await import(
            "firebase/auth"
          );

          // Sign in with email and password
          const signInResult = await signInWithEmailAndPassword(
            getAuth(),
            values.email,
            values.password
          );

          onSuccess?.();

          // if no error, will continue past if statement
          analytics.identify(signInResult.user.uid, {
            ...signInResult.user,
          });
          // segment.login("email/password", values.email);
        } catch (error) {
          if (error instanceof FirebaseError) {
            onError?.(error);
            setSubmitError(
              firebaseCodeErrorsMap.get(error.code).message ??
                "Unable to process log in request"
            );
          } else {
            throw error;
          }
        }
      },
    });

  return (
    <>
      {children}
      <SocialSignOn text="Continue" onError={(msg) => {}} />
      <form onSubmit={handleSubmit} className="mt-8">
        <Input
          id="email"
          placeholder="Email"
          type="email"
          name="email"
          autoComplete="email"
          value={values.email}
          onChange={handleChange}
        />
        <Input
          id="password"
          type="password"
          placeholder="Password"
          name="password"
          autoComplete="current-password"
          value={values.password}
          onChange={handleChange}
        />
        <PrimaryBtn
          type="submit"
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          Log In
        </PrimaryBtn>
      </form>
    </>
  );
};
